import React, { ComponentType, memo, Ref, SyntheticEvent, useCallback, useMemo } from 'react'
import cn from 'classnames'
import {
  defaultGetOptionId,
  defaultGetOptionName,
  defaultLoadingMessage,
  defaultNoOptionsMessage
} from 'components/helpers/defaultValues'
import debounce from 'lodash/debounce'
import noop from 'lodash/noop'

import { areOptionsEqual } from './helpers/areOptionsEqual'
import { Option } from './Option'
import { BaseOptionType, IOptionClassNames, IOptionProps } from './types'

import s from './BaseSelect.module.less'

export interface IOptionsScrollBlockProps<Option extends BaseOptionType> {
  /**
   * Ref для опции, в состоянии hover
   * Нужен для навигации с клавиатуры (конкретно для скролла)
   */
  hoverOptionRef: Ref<HTMLLIElement>
  /**
   * Коллбек, выполняемый при выборе опции
   */
  onChange: (options: Option) => void
  /**
   * Сеттер для "optionHoverIndex"
   */
  onSetOptionHoverIndex: (index: number) => void
  /**
   * Индекс опции, которая находится в состоянии hover
   */
  optionHoverIndex: number
  /**
   * Опции для выбора
   */
  options: Option[]
  /**
   * Ширина селекта на странице (актуальна только для десктоп разрешния)
   */
  selectWidth: number
  /**
   * Компонент для кастомной отрисовки опций
   */
  CustomOptionComponent?: ComponentType<IOptionProps<Option>>
  /**
   * Конфиг с именами классов для OptionList
   */
  classNames?: IOptionClassNames
  /**
   * Коллбек для получения id опций, используется при проставлении React key
   * и сравнения опций (выбрана ли текущая опция)
   */
  getOptionId?: (o?: Option) => IdType
  /**
   * Коллбек для получения имени опции
   */
  getOptionName?: (o?: Option) => string
  /**
   * Флаг, если выставлен в true - то выпадающий список растягивается на ширину самой длинной опции
   */
  hasAutoOptionListWidth?: boolean
  /**
   * Наличие индикатора загрузки
   */
  isLoading?: boolean
  /**
   * На мобильном разрешении (от 699px) (true)
   * или на десктопе (false)
   */
  isMobile?: boolean
  /**
   * Сообщение, отображаемое, когда происходит загрузка
   */
  loadingMessage?: string
  /**
   * Максимальная высота списка опций
   */
  maxOptionListHeight?: number
  /**
   * Максимальная ширина выпадающего списка,
   * по умолчанию равна ширине селекта
   */
  maxOptionListWidth?: number
  /**
   * Минимальная ширина выпадающего списка
   */
  minOptionListWidth?: number
  /**
   * Сообщение, отображаемое, когда в селекте нет опций
   */
  noOptionsMessage?: string
  /**
   * Коллбек, выполняемый при скролле выпадающего списка
   */
  onScrollOptionList?: (event: SyntheticEvent<HTMLDivElement>) => void
  /**
   * Показывать / не показывать сообщение о загрузке
   */
  showLoadingMessage?: boolean
  /**
   * Выбранная опция/опции
   */
  value?: Option | Option[]
}

const OptionsScrollBlockNoMemo = <T extends BaseOptionType>({
  hoverOptionRef,
  selectWidth,
  classNames = {},
  value: selectedValue,
  options = [],
  optionHoverIndex,
  maxOptionListHeight = 210,
  isLoading = false,
  showLoadingMessage = isLoading,
  loadingMessage = defaultLoadingMessage,
  noOptionsMessage = defaultNoOptionsMessage,
  onSetOptionHoverIndex = noop,
  onChange = noop,
  onScrollOptionList = noop,
  getOptionId = defaultGetOptionId,
  getOptionName = defaultGetOptionName,
  CustomOptionComponent
}: IOptionsScrollBlockProps<T>) => {
  const renderOption = useCallback(
    (currentOption: T, index: number) => {
      const isSelected = Array.isArray(selectedValue)
        ? selectedValue.some((selectedOption) => areOptionsEqual(getOptionId, currentOption, selectedOption))
        : areOptionsEqual(getOptionId, currentOption, selectedValue)

      const isHovered = optionHoverIndex === index

      const key = getOptionId(currentOption) || index

      const generalOptionProps: IOptionProps<T> = {
        hoverOptionRef: isHovered ? hoverOptionRef : undefined,
        className: classNames.option,
        name: getOptionName(currentOption),
        value: currentOption,
        index,
        selectWidth,
        isMobile: false,
        isSelected,
        isHovered,
        isLoading,
        onClick: onChange,
        onSetOptionHoverIndex
      }

      if (CustomOptionComponent) {
        return <CustomOptionComponent key={key} {...generalOptionProps} />
      }

      return <Option key={key} {...generalOptionProps} />
    },
    [
      hoverOptionRef,
      selectedValue,
      optionHoverIndex,
      classNames,
      selectWidth,
      isLoading,
      onChange,
      onSetOptionHoverIndex,
      getOptionId,
      getOptionName,
      CustomOptionComponent
    ]
  )

  const renderNoOptionMessage = useCallback(
    (message: string) => <p className={cn(s.noOptionMessageText, classNames.noOptionMessageText)}>{message}</p>,
    [classNames]
  )

  const renderOptionList = useCallback(
    (options: T[]) => {
      if (showLoadingMessage) {
        return renderNoOptionMessage(loadingMessage)
      }

      if (options.length === 0) {
        return renderNoOptionMessage(noOptionsMessage)
      }

      return (
        <ul className={cn(s.optionList, classNames.optionList)} style={{ maxHeight: maxOptionListHeight }}>
          {options.map(renderOption)}
        </ul>
      )
    },
    [
      showLoadingMessage,
      maxOptionListHeight,
      classNames.optionList,
      renderOption,
      renderNoOptionMessage,
      loadingMessage,
      noOptionsMessage
    ]
  )

  const handleScroll = useMemo(() => debounce(onScrollOptionList, 150), [onScrollOptionList])

  return <div onScroll={handleScroll}>{renderOptionList(options)}</div>
}

export const OptionsScrollBlock = memo(OptionsScrollBlockNoMemo) as typeof OptionsScrollBlockNoMemo
